import {uploadPreviewCacheState} from './upload-preview-cache-state'

const getUploadBatchSignature = ({files, results}) => JSON.stringify({
    files: (files || []).map(file => file.name || file).sort(),
    results: (results || []).map(result => ({
        file: result.file,
        uploadedFilename: result.uploadedFilename,
        status: result.status,
        itemId: result.result?.item?.id,
    })).sort((a, b) => String(a.file).localeCompare(String(b.file))),
})

export const uploadState = {
    defaultState: {
        ...uploadPreviewCacheState.defaultState,
        importFiles: null,
        uploadProgress: null,
        uploadResults: [],
        uploadHistory: [],
        noLocationItems: null,
        noLocationPage: null,
        pendingActionItems: null,
        pendingActionPage: null,
        uploadConflictFiles: null,
        uploadConflictPage: null,
        resumedUploads: null,
    },
    actions: {
        ...uploadPreviewCacheState.actions,
        startFileImport: (files, state) => {
            const history = state.importFiles?.length
                ? [{files: state.importFiles, results: state.uploadResults, timestamp: Date.now()}, ...state.uploadHistory]
                : state.uploadHistory
            return {...state, importFiles: files, uploadProgress: {}, privateMode: true, uploadHistory: history}
        },
        clearFileImport: (_, state) => ({...state, importFiles: null, uploadProgress: null, uploadResults: [], privateMode: false}),
        setUploadProgress: ({file, ...progress}, state) => ({...state, uploadProgress: {...state.uploadProgress, [file]: progress}}),
        setNoLocationItems: (items, state) => ({...state, noLocationItems: items, noLocationPage: null}),
        setNoLocationPage: (page, state) => ({
            ...state,
            noLocationItems: page.items || [],
            noLocationPage: pageMetadata(page),
        }),
        removeNoLocationItem: (id, state) => ({...state, noLocationItems: (state.noLocationItems || []).filter(i => i.id !== id)}),
        setPendingActionItems: (items, state) => ({...state, pendingActionItems: items, pendingActionPage: null}),
        setPendingActionPage: (page, state) => ({
            ...state,
            pendingActionItems: page.items || [],
            pendingActionPage: pageMetadata(page),
        }),
        appendPendingActionPage: (page, state) => ({
            ...state,
            pendingActionItems: appendUnique(state.pendingActionItems, page.items, item => item.id),
            pendingActionPage: pageMetadata(page, state.pendingActionPage),
        }),
        removePendingActionItem: (id, state) => ({...state, pendingActionItems: (state.pendingActionItems || []).filter(i => i.id !== id)}),
        addPendingActionItem: (item, state) => {
            const pendingActionItems = state.pendingActionItems || []
            if (item?.id && pendingActionItems.some(i => i.id === item.id)) return state
            return {...state, pendingActionItems: [item, ...pendingActionItems]}
        },
        updatePendingActionItemLocation: ({
            id, country, city, coords, locationSource = 'user', resolution,
            albumCreated, albumMadePrivate, albumDateFrom, albumDateTo,
            cityVisitId, status, private: isPrivate, privacyOverride,
        }, state) => ({
            ...state,
            pendingActionItems: (state.pendingActionItems || []).map(i => i.id === id
                ? {
                    ...i, country, city, coords, locationSource, needsLocation: false,
                    albumCreated: albumCreated ?? i.albumCreated,
                    albumMadePrivate: albumMadePrivate ?? i.albumMadePrivate,
                    albumDateFrom: albumDateFrom ?? i.albumDateFrom,
                    albumDateTo: albumDateTo ?? i.albumDateTo,
                    cityVisitId: cityVisitId ?? i.cityVisitId,
                    status: status ?? i.status,
                    private: isPrivate ?? i.private,
                    privacyOverride: privacyOverride === undefined ? i.privacyOverride : privacyOverride,
                    locationCandidateIds: null, locationCandidates: null, resolution,
                }
                : i),
        }),
        updatePendingActionItemDate: ({id, date}, state) => ({
            ...state,
            pendingActionItems: (state.pendingActionItems || []).map(i => i.id === id ? {...i, date, timeSource: 'user'} : i),
        }),
        setResumedUploads: (files, state) => ({...state, resumedUploads: files}),
        setUploadConflictFiles: (files, state) => ({...state, uploadConflictFiles: files, uploadConflictPage: null}),
        setUploadConflictPage: (page, state) => ({
            ...state,
            uploadConflictFiles: page.files || [],
            uploadConflictPage: pageMetadata(page),
        }),
        appendUploadConflictPage: (page, state) => ({
            ...state,
            uploadConflictFiles: appendUnique(state.uploadConflictFiles, page.files, file => file.id || file.filename),
            uploadConflictPage: pageMetadata(page, state.uploadConflictPage),
        }),
        removeUploadConflictFile: (filename, state) => ({...state, uploadConflictFiles: (state.uploadConflictFiles || []).filter(f => f.filename !== filename)}),
        resolveUploadItem: ({uploadItem, filename, result, status, error, resolution}, state) => {
            const fileName = uploadItem?.name || uploadItem
            const uploadedFilename = filename || uploadItem?.uploadedFilename || fileName
            const nextStatus = status || (resolution?.action === 'cancel' ? 'cancelled' : 'completed')
            const resolvedResult = item => ({
                ...(item || {}),
                file: fileName,
                status: nextStatus,
                error: error || item?.error || null,
                result: result || item?.result || uploadItem?.result || null,
                existingItem: item?.existingItem || uploadItem?.existingItem || null,
                uploadedFilename: item?.uploadedFilename || uploadedFilename || null,
                conflictResolved: resolution?.type === 'conflict' ? true : item?.conflictResolved,
                conflictResolution: resolution?.type === 'conflict' ? resolution : item?.conflictResolution,
                resolution,
            })
            const updateResults = results => updateMatchingUploadResult(results, uploadItem, resolvedResult)
            const current = shouldUpdateCurrentUploadResults(uploadItem)
                ? updateResults(state.uploadResults)
                : {updated: state.uploadResults, found: false}
            const uploadHistory = shouldUpdateUploadHistory(uploadItem)
                ? state.uploadHistory.map(batch => ({...batch, results: updateResults(batch.results).updated}))
                : state.uploadHistory
            const uploadConflictFiles = (state.uploadConflictFiles || []).map(file => file.filename === uploadedFilename
                ? {
                    ...file,
                    status: nextStatus,
                    result: result || file.result || null,
                    conflictResolved: resolution?.type === 'conflict' ? true : file.conflictResolved,
                    conflictResolution: resolution?.type === 'conflict' ? resolution : file.conflictResolution,
                    resolution,
                }
                : file)
            return {
                ...state,
                uploadResults: current.updated,
                uploadHistory,
                uploadConflictFiles,
            }
        },
        markUploadItemDeleted: (uploadItem, state) => {
            const fileName = uploadItem?.name || uploadItem
            const id = uploadItem?.result?.item?.id
            const deletedResult = result => ({
                ...(result || {}),
                file: fileName,
                status: 'deleted',
                result: result?.result || uploadItem?.result,
                existingItem: result?.existingItem || uploadItem?.existingItem || null,
                uploadedFilename: result?.uploadedFilename || uploadItem?.uploadedFilename || null,
                conflictResolved: result?.conflictResolved || uploadItem?.status === 'conflict' || uploadItem?.persistentConflict || false,
                resolution: {type: 'delete'},
            })
            const updateResults = results => updateMatchingUploadResult(results, uploadItem, deletedResult)
            const current = shouldUpdateCurrentUploadResults(uploadItem)
                ? updateResults(state.uploadResults)
                : {updated: state.uploadResults, found: false}
            let found = current.found
            const uploadHistory = shouldUpdateUploadHistory(uploadItem)
                ? state.uploadHistory.map(batch => {
                    const batchResults = updateResults(batch.results)
                    if (batchResults.found) found = true
                    return {...batch, results: batchResults.updated}
                })
                : state.uploadHistory
            const pendingActionItems = updateMatchingMediaItems(state.pendingActionItems, id, item => ({
                ...item,
                deletedAt: item.deletedAt || Date.now(),
                status: 'deleted',
                resolution: {type: 'delete'},
            }))
            const noLocationItems = updateMatchingMediaItems(state.noLocationItems, id, item => ({
                ...item,
                deletedAt: item.deletedAt || Date.now(),
                status: 'deleted',
                resolution: {type: 'delete'},
            }))
            found = found || pendingActionItems.found || noLocationItems.found
            if (found) return {
                ...state,
                uploadResults: current.updated,
                uploadHistory,
                pendingActionItems: pendingActionItems.updated,
                noLocationItems: noLocationItems.updated,
            }

            return {
                ...state,
                uploadResults: current.updated,
                uploadHistory: [{
                    files: [{name: fileName, size: uploadItem?.size}],
                    results: [deletedResult(null)],
                    timestamp: Date.now(),
                }, ...uploadHistory],
            }
        },
        updateNoLocationItemLocation: ({id, country, city, coords, locationSource = 'user', resolution}, state) => ({
            ...state,
            noLocationItems: (state.noLocationItems || []).map(i => i.id === id
                ? {...i, country, city, coords, locationSource, needsLocation: false, locationCandidateIds: null, locationCandidates: null, resolution}
                : i),
        }),
        setUploadBatchResults: ({files, results}, state) => ({
            ...state,
            uploadResults: state.importFiles === files ? results : state.uploadResults,
            uploadHistory: state.uploadHistory.map(batch => batch.files === files ? {...batch, results} : batch),
        }),
        updateUploadItemLocation: ({
            id, fileName, country, city, coords, locationSource = 'user', resolution,
            albumCreated, albumMadePrivate, albumDateFrom, albumDateTo,
            cityVisitId, status, private: isPrivate, privacyOverride
        }, state) => {
            const updateResults = results => results.map(r =>
                ((id && r.result?.item?.id === id) || (!id && r.file === fileName && !r.uploadedFilename)) && r.result?.item
                    ? {...r, result: {...r.result, item: {
                        ...r.result.item, country, city, coords, locationSource, needsLocation: false,
                        albumCreated: albumCreated ?? r.result.item.albumCreated,
                        albumMadePrivate: albumMadePrivate ?? r.result.item.albumMadePrivate,
                        albumDateFrom: albumDateFrom ?? r.result.item.albumDateFrom,
                        albumDateTo: albumDateTo ?? r.result.item.albumDateTo,
                        cityVisitId: cityVisitId ?? r.result.item.cityVisitId,
                        private: isPrivate ?? r.result.item.private,
                        privacyOverride: privacyOverride === undefined ? r.result.item.privacyOverride : privacyOverride,
                        locationCandidateIds: null, locationCandidates: null
                    }}, status: status || r.status, resolution: resolution || {type: 'location', source: 'manual'}}
                    : r
            )
            return {
                ...state,
                uploadResults: updateResults(state.uploadResults),
                uploadHistory: state.uploadHistory.map(batch => ({...batch, results: updateResults(batch.results)})),
            }
        },
        updateUploadItemDate: ({id, fileName, date}, state) => {
            const updateResults = results => results.map(r =>
                ((id && r.result?.item?.id === id) || (!id && r.file === fileName && !r.uploadedFilename)) && r.result?.item
                    ? {...r, result: {...r.result, item: {...r.result.item, date, timeSource: 'user'}}}
                    : r
            )
            return {
                ...state,
                uploadResults: updateResults(state.uploadResults),
                uploadHistory: state.uploadHistory.map(batch => ({...batch, results: updateResults(batch.results)})),
            }
        },
        archiveUploadBatch: ({files, results}, state) => {
            const signature = getUploadBatchSignature({files, results})
            const history = state.uploadHistory || []
            const historyIndex = history.findIndex(batch => batch.files === files)
            if (historyIndex !== -1) return {
                ...state,
                uploadHistory: history.map((batch, index) => index === historyIndex ? {...batch, results, signature} : batch),
            }
            const alreadyArchived = history.some(batch => batch.signature === signature || getUploadBatchSignature(batch) === signature)
            return {
                ...state,
                importFiles: state.importFiles === files ? null : state.importFiles,
                uploadResults: state.importFiles === files ? [] : state.uploadResults,
                uploadHistory: files?.length && !alreadyArchived
                    ? [{files, results, timestamp: Date.now(), signature}, ...history]
                    : history,
            }
        },
    },
}

const pageMetadata = (page, previous = {}) => ({
    pageNumber: page.pageNumber ?? 0,
    lastPage: page.lastPage === true,
    snapshotAt: page.snapshotAt || previous.snapshotAt || null,
    total: page.total ?? previous.total ?? 0,
    noLocationTotal: page.noLocationTotal ?? previous.noLocationTotal ?? null,
})
const appendUnique = (current, next, keyOf) => {
    const items = [...(current || [])]
    const keys = new Set(items.map(keyOf))
    for (const item of next || []) {
        const key = keyOf(item)
        if (keys.has(key)) continue
        keys.add(key)
        items.push(item)
    }
    return items
}

const shouldUpdateCurrentUploadResults = uploadItem => !String(uploadItem?.$uploadKey || '').startsWith('history:')
const shouldUpdateUploadHistory = uploadItem => !String(uploadItem?.$uploadKey || '').startsWith('current:')
const updateMatchingMediaItems = (items, id, update) => {
    let found = false
    return {
        updated: (items || []).map(item => {
            if (found || !id || item.id !== id) return item
            found = true
            return update(item)
        }),
        found,
    }
}
const updateMatchingUploadResult = (results, uploadItem, update) => {
    let found = false
    return {
        updated: (results || []).map((result, index) => {
            if (found || !isSameUploadResult(result, uploadItem, index)) return result
            found = true
            return update(result)
        }),
        found,
    }
}
const isSameUploadResult = (result, uploadItem, index) => {
    if (result.uploadedFilename && uploadItem?.uploadedFilename)
        return result.uploadedFilename === uploadItem.uploadedFilename
    if (result.result?.item?.id && uploadItem?.result?.item?.id)
        return result.result.item.id === uploadItem.result.item.id
    if (uploadItem?.$resultIndex != null)
        return index === uploadItem.$resultIndex && result.file === uploadItem.name
    return result.file === (uploadItem?.name || uploadItem)
}
